import { useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Plus,
  RefreshCw,
  Wifi,
  WifiOff,
  Zap,
} from "lucide-react";
import { MetricChart } from "../components/MetricChart";
import { EvidenceViewer, Evidence } from "../components/EvidenceViewer";
import { StatusBadge } from "../components/StatusBadge";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  ModalFooterButtons,
} from "../components/Modal";

const complianceMetrics = [
  { date: "Mar 3", score: 84, coverage: 91, drift: 3 },
  { date: "Mar 4", score: 85, coverage: 92, drift: 2 },
  { date: "Mar 5", score: 83, coverage: 90, drift: 4 },
  { date: "Mar 6", score: 86, coverage: 93, drift: 2 },
  { date: "Mar 7", score: 87, coverage: 93, drift: 1 },
  { date: "Mar 8", score: 87, coverage: 94, drift: 1 },
  { date: "Mar 9", score: 87, coverage: 95, drift: 1 },
];

const evidenceSources: Evidence[] = [
  {
    id: "es1",
    title: "AWS CloudTrail - All Regions",
    source: "AWS",
    type: "log",
    status: "pass",
    collectedAt: "5 min ago",
    controlId: "AU-2",
    description:
      "Continuous collection of all API calls across AWS accounts. 15,847 events ingested in last 24 hours.",
  },
  {
    id: "es2",
    title: "Azure AD Sign-In Logs",
    source: "Azure",
    type: "log",
    status: "pass",
    collectedAt: "12 min ago",
    controlId: "IA-2",
    description:
      "Authentication events from Azure Active Directory. MFA enforcement verified for 100% of user accounts.",
  },
  {
    id: "es3",
    title: "CrowdStrike Falcon Detection",
    source: "CrowdStrike",
    type: "api",
    status: "pass",
    collectedAt: "8 min ago",
    controlId: "SI-3",
    description:
      "Real-time EDR telemetry from 847 enrolled endpoints. Zero active threats detected.",
  },
  {
    id: "es4",
    title: "AWS Config - Configuration Changes",
    source: "AWS",
    type: "config",
    status: "fail",
    collectedAt: "1 hr ago",
    controlId: "CM-6",
    description:
      "3 unauthorized configuration changes detected on EC2 instances. POAM-040 created.",
    url: "#",
  },
  {
    id: "es5",
    title: "Splunk SIEM - Security Events",
    source: "Splunk",
    type: "log",
    status: "pass",
    collectedAt: "2 min ago",
    controlId: "AU-3",
    description:
      "Aggregated security events from 28 data sources. Alert correlation running.",
  },
  {
    id: "es6",
    title: "HashiCorp Vault - Secrets Access",
    source: "HashiCorp",
    type: "log",
    status: "pass",
    collectedAt: "15 min ago",
    controlId: "IA-5",
    description:
      "All secrets access logged and audited. No unauthorized access attempts in last 24 hours.",
  },
  {
    id: "es7",
    title: "Tenable.io - Vulnerability Scans",
    source: "Tenable",
    type: "api",
    status: "pending",
    collectedAt: "3 hrs ago",
    controlId: "SI-2",
    description:
      "Scheduled vulnerability scan in progress for production subnet 10.0.0.0/16.",
  },
];

const driftAlerts = [
  {
    id: "d1",
    control: "CM-6.1",
    asset: "AWS-EC2-PRD-002",
    severity: "high",
    description:
      "Docker daemon running with privileged mode disabled - expected enabled per config baseline",
    detected: "2 hrs ago",
    status: "open",
  },
  {
    id: "d2",
    control: "CM-6",
    asset: "AWS-EC2-PRD-001",
    severity: "medium",
    description:
      "Security group rule added outside of IaC pipeline: ingress TCP 8080 from 0.0.0.0/0",
    detected: "3 hrs ago",
    status: "in-progress",
  },
  {
    id: "d3",
    control: "AU-9",
    asset: "AWS-S3-PRD-LOGS",
    severity: "high",
    description: "S3 bucket logging disabled on audit log archive bucket",
    detected: "5 hrs ago",
    status: "open",
  },
  {
    id: "d4",
    control: "AC-3",
    asset: "AWS IAM",
    severity: "low",
    description:
      "IAM policy attached directly to user account (bypassing role-based access)",
    detected: "1 day ago",
    status: "in-progress",
  },
];

const sourceStats = [
  {
    label: "Active Sources",
    value: "12",
    icon: Wifi,
    color: "text-emerald-500",
  },
  { label: "Disconnected", value: "1", icon: WifiOff, color: "text-red-500" },
  {
    label: "Drift Alerts",
    value: "4",
    icon: AlertTriangle,
    color: "text-amber-500",
  },
  {
    label: "Evidence Items",
    value: "1,847",
    icon: CheckCircle2,
    color: "text-foreground",
  },
];

/* ── Assessment Triggers (REQ-8.5) ──────────────────────── */
const assessmentTriggers = [
  {
    id: "at1",
    name: "POAM Overdue Trigger",
    condition: "poam_items.due_date < NOW() AND status != 'resolved'",
    action: "Create POA&M overdue Kafka event + escalate workflow",
    target: "oscal-poam",
    status: "active",
    lastFired: "2026-03-09 00:01",
    executions: 4,
  },
  {
    id: "at2",
    name: "Drift → Assessment Trigger",
    condition:
      "drift_detections.severity IN ('high','critical') AND NOT acknowledged",
    action: "Auto-create assessment activity on open assessment plan",
    target: "oscal-assessment",
    status: "active",
    lastFired: "2026-03-09 02:15",
    executions: 2,
  },
  {
    id: "at3",
    name: "CVE Critical Trigger",
    condition:
      "raw_evidence.cve_severity = 'CRITICAL' AND source_type = 'vulnerability_scanner'",
    action: "Create direct finding (Type 2) + notify ISSO",
    target: "oscal-assessment",
    status: "active",
    lastFired: "2026-03-08 22:00",
    executions: 1,
  },
  {
    id: "at4",
    name: "FedRAMP 20x KSI Evidence",
    condition:
      "compliance_metrics.ksi_coverage < 0.95 AND framework = 'fedramp-20x'",
    action:
      "Flag KSI control set as needs_review + schedule evidence collection",
    target: "telemetry",
    status: "active",
    lastFired: "2026-03-07 06:00",
    executions: 0,
  },
  {
    id: "at5",
    name: "Continuous Monitoring Trigger",
    condition: "evidence_collection_runs.drift_count > 5 WITHIN 24h",
    action: "Initiate unscheduled assessment plan on affected system",
    target: "oscal-assessment",
    status: "disabled",
    lastFired: "Never",
    executions: 0,
  },
];

export function Telemetry() {
  const [activeTab, setActiveTab] = useState<
    "sources" | "metrics" | "drift" | "triggers"
  >("sources");
  const [showAddSource, setShowAddSource] = useState(false);
  const [sourceForm, setSourceForm] = useState({
    title: "",
    source: "",
    type: "",
    controlId: "",
    description: "",
    url: "",
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>Telemetry & Evidence</h1>
          <p className="text-sm d mt-0.5">
            Continuous monitoring, evidence collection and drift detection
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          <button className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent transition-colors">
            <RefreshCw className="size-4" />
            Sync All
          </button>
          <button
            onClick={() => setShowAddSource(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="size-4" />
            Add Source
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {sourceStats.map(({ label, value, icon: Icon, color }) => (
          <div
            key={label}
            className="rounded-lg border border-border bg-card p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs d">{label}</p>
              <Icon className={`size-4 ${color}`} />
            </div>
            <p className="text-2xl font-semibold text-foreground">{value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 border-b border-border overflow-x-auto">
        {(
          [
            { key: "sources", label: "Evidence Sources" },
            { key: "metrics", label: "Compliance Metrics" },
            { key: "drift", label: "Drift Detection" },
            { key: "triggers", label: "Assessment Triggers" },
          ] as const
        ).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
              activeTab === key
                ? "border-foreground text-foreground"
                : "border-transparent d hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === "sources" && <EvidenceViewer evidence={evidenceSources} />}

      {activeTab === "metrics" && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="text-sm font-medium mb-1">
              Compliance Score (7 days)
            </h3>
            <p className="text-xs d mb-4">Rolling daily score</p>
            <MetricChart
              type="area"
              data={complianceMetrics}
              dataKeys={[
                {
                  key: "score",
                  label: "Score",
                  color: "hsl(var(--foreground))",
                },
              ]}
              xKey="date"
              height={180}
            />
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="text-sm font-medium mb-1">Evidence Coverage (%)</h3>
            <p className="text-xs d mb-4">Controls with automated evidence</p>
            <MetricChart
              type="area"
              data={complianceMetrics}
              dataKeys={[
                { key: "coverage", label: "Coverage %", color: "#22c55e" },
              ]}
              xKey="date"
              height={180}
            />
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="text-sm font-medium mb-1">Active Drift Alerts</h3>
            <p className="text-xs d mb-4">
              Configuration drift events detected
            </p>
            <MetricChart
              type="bar"
              data={complianceMetrics}
              dataKeys={[
                { key: "drift", label: "Drift Alerts", color: "#f59e0b" },
              ]}
              xKey="date"
              height={180}
            />
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="text-sm font-medium mb-3">
              Evidence Collection by Source
            </h3>
            <div className="space-y-2">
              {[
                { source: "AWS CloudTrail", count: 15847, pct: 42 },
                { source: "Splunk SIEM", count: 8234, pct: 22 },
                { source: "Azure AD", count: 5621, pct: 15 },
                { source: "CrowdStrike", count: 4102, pct: 11 },
                { source: "HashiCorp Vault", count: 2318, pct: 6 },
                { source: "Tenable", count: 1526, pct: 4 },
              ].map((row) => (
                <div
                  key={row.source}
                  className="flex items-center gap-3 text-sm"
                >
                  <span className="w-36 text-xs d truncate">{row.source}</span>
                  <div className="flex-1 bg-muted rounded-full h-1.5">
                    <div
                      className="bg-foreground h-1.5 rounded-full"
                      style={{ width: `${row.pct}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono w-16 text-right">
                    {row.count.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "drift" && (
        <div className="space-y-3">
          {driftAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`rounded-lg border bg-card p-4 ${
                alert.severity === "high"
                  ? "border-orange-500/30"
                  : alert.severity === "medium"
                    ? "border-amber-500/20"
                    : "border-border"
              }`}
            >
              <div className="flex items-start gap-3 flex-wrap">
                <AlertTriangle
                  className={`size-4 mt-0.5 flex-shrink-0 ${
                    alert.severity === "high"
                      ? "text-orange-500"
                      : alert.severity === "medium"
                        ? "text-amber-500"
                        : "d"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono d">{alert.control}</span>
                    <span className="text-xs d font-mono">·</span>
                    <span className="text-xs font-mono d">{alert.asset}</span>
                    <span className="text-xs d ml-auto">{alert.detected}</span>
                  </div>
                  <p className="text-sm mt-1">{alert.description}</p>
                </div>
                <StatusBadge status={alert.status as "open" | "in-progress"} />
              </div>
              <div className="flex items-center gap-2 mt-3 pt-2 border-t border-border">
                <button className="text-xs d hover:text-foreground transition-colors">
                  New POAM
                </button>
                <span className="text-xs d">·</span>
                <button className="text-xs d hover:text-foreground transition-colors">
                  Dismiss
                </button>
                <span className="text-xs d">·</span>
                <button className="text-xs d hover:text-foreground transition-colors">
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === "triggers" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs d">
              Assessment triggers (REQ-8.5) fire when telemetry conditions are
              met - automatically creating POA&M items or assessment activities
              via Kafka events.
            </p>
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity flex-shrink-0 whitespace-nowrap">
              <Plus className="size-3.5" /> New Trigger
            </button>
          </div>
          <div className="space-y-3">
            {assessmentTriggers.map((trigger) => (
              <div
                key={trigger.id}
                className="rounded-lg border border-border bg-card p-4"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-start gap-3">
                    <div
                      className={`size-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        trigger.status === "active"
                          ? "bg-emerald-100 dark:bg-emerald-500/20"
                          : "bg-muted"
                      }`}
                    >
                      <Zap
                        className={`size-4 ${trigger.status === "active" ? "text-emerald-600 dark:text-emerald-400" : "d"}`}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{trigger.name}</p>
                      <p className="text-xs d mt-0.5">
                        Target module:{" "}
                        <span className="font-mono">{trigger.target}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        trigger.status === "active"
                          ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                          : "bg-slate-100 dark:bg-white/[0.07] text-slate-500 dark:text-white/40"
                      }`}
                    >
                      {trigger.status}
                    </span>
                    <span className="text-xs d">
                      {trigger.executions} fired
                    </span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] font-bold d tracking-wide w-16 flex-shrink-0 pt-0.5">
                      Condition
                    </span>
                    <code className="text-[10px] font-mono bg-muted rounded px-2 py-1 flex-1 d leading-relaxed break-all">
                      {trigger.condition}
                    </code>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] font-bold d tracking-wide w-16 flex-shrink-0 pt-0.5">
                      Action
                    </span>
                    <p className="text-xs text-foreground/70 flex-1">
                      {trigger.action}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold d tracking-wide w-16 flex-shrink-0">
                      Last Fired
                    </span>
                    <span className="text-xs font-mono d">
                      {trigger.lastFired}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Evidence Source Modal */}
      <Modal
        open={showAddSource}
        onClose={() => setShowAddSource(false)}
        title="Add Evidence Source"
        description="Connect a new telemetry or evidence collection source"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAddSource(false)}
            onSubmit={() => {
              setShowAddSource(false);
              setSourceForm({
                title: "",
                source: "",
                type: "",
                controlId: "",
                description: "",
                url: "",
              });
            }}
            submitLabel="Save"
            submitDisabled={!sourceForm.title || !sourceForm.type}
          />
        }
      >
        <FormField label="Source Name" required>
          <FormInput
            placeholder="e.g. AWS CloudTrail - Production Account"
            value={sourceForm.title}
            onChange={(v) => setSourceForm((f) => ({ ...f, title: v }))}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Provider / Platform">
            <FormSelect
              options={[
                "AWS",
                "Azure",
                "GCP",
                "CrowdStrike",
                "Splunk",
                "HashiCorp",
                "Tenable",
                "Qualys",
                "Palo Alto",
                "Other",
              ]}
              placeholder="Select provider"
              value={sourceForm.source}
              onChange={(v) => setSourceForm((f) => ({ ...f, source: v }))}
            />
          </FormField>
          <FormField label="Collection Type" required>
            <FormSelect
              options={["log", "api", "config", "scan", "webhook"]}
              placeholder="Select type"
              value={sourceForm.type}
              onChange={(v) => setSourceForm((f) => ({ ...f, type: v }))}
            />
          </FormField>
        </div>
        <FormField
          label="Primary Control"
          hint="The OSCAL control this source primarily satisfies"
        >
          <FormInput
            placeholder="e.g. AU-2"
            value={sourceForm.controlId}
            onChange={(v) => setSourceForm((f) => ({ ...f, controlId: v }))}
          />
        </FormField>
        <FormField
          label="Endpoint / Connection URL"
          hint="API endpoint or connection string for this source"
        >
          <FormInput
            placeholder="https://api.example.com/v1/events or arn:aws:"
            value={sourceForm.url}
            onChange={(v) => setSourceForm((f) => ({ ...f, url: v }))}
          />
        </FormField>
        <FormField label="Description">
          <FormTextarea
            placeholder="Describe what this source collects and how it supports compliance evidence"
            value={sourceForm.description}
            onChange={(v) => setSourceForm((f) => ({ ...f, description: v }))}
          />
        </FormField>
      </Modal>
    </div>
  );
}
